import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { 
  ArrowLeft, Brain, Lock, CheckCircle2, Circle, Award, 
  Clock, ChevronRight, Sparkles, Heart, BookOpen
} from "lucide-react";
import { API, useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";

const EmotionalIntelligencePage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [modules, setModules] = useState([]);
  const [progress, setProgress] = useState({ completed: 0, total: 22, percentage: 0 });
  const [isPremium, setIsPremium] = useState(false);
  const [hasBadge, setHasBadge] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchModules();
  }, []);

  const fetchModules = async () => {
    try {
      const response = await axios.get(`${API}/emotional-intelligence/modules`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setModules(response.data.modules);
      setProgress(response.data.progress);
      setIsPremium(response.data.is_premium);
      setHasBadge(response.data.has_completion_badge);
    } catch (error) {
      console.error("Error fetching modules:", error);
      toast.error("Failed to load modules");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen hero-gradient flex items-center justify-center">
        <div className="animate-breathe">
          <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
            <Brain className="w-8 h-8 text-primary" strokeWidth={1.5} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen hero-gradient pb-24" data-testid="ei-page">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border/50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate("/dashboard")}
            className="text-muted-foreground hover:text-foreground"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Dashboard
          </Button>
          <div className="flex items-center gap-2">
            <Brain className="w-5 h-5 text-primary" strokeWidth={1.5} />
            <span className="font-heading text-lg font-semibold hidden sm:inline">Emotional Intelligence</span>
          </div>
          <div className="w-24"></div>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center mx-auto mb-6">
            <Brain className="w-10 h-10 text-primary" strokeWidth={1.5} />
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-semibold text-foreground mb-3">
            Emotional Intelligence & Regulation
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            22 transformative modules to deepen your emotional awareness, build resilience, and cultivate meaningful connections.
          </p>
        </div>

        {/* Progress Card */}
        <Card className="glass-card rounded-3xl border-0 mb-8">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-heading text-lg font-semibold text-foreground">Your Progress</h2>
                <p className="text-sm text-muted-foreground">
                  {progress.completed} of {progress.total} modules completed
                </p>
              </div>
              {hasBadge && (
                <div className="flex items-center gap-2 bg-gradient-to-r from-amber-100 to-amber-50 dark:from-amber-900/30 dark:to-amber-800/20 px-4 py-2 rounded-full border border-amber-200 dark:border-amber-700">
                  <Award className="w-5 h-5 text-amber-600" />
                  <span className="text-sm font-medium text-amber-700 dark:text-amber-300">Mastery Badge</span>
                </div>
              )}
            </div>
            <Progress value={progress.percentage} className="h-3" />
            <p className="text-xs text-muted-foreground mt-2 text-right">{progress.percentage}% complete</p>
          </CardContent>
        </Card>

        {/* Access Info */}
        {!isPremium && (
          <Card className="glass-card rounded-2xl border-0 mb-8 bg-gradient-to-r from-primary/5 to-accent/5">
            <CardContent className="p-5">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="font-medium text-foreground">Unlock All 22 Modules</h3>
                  <p className="text-sm text-muted-foreground">
                    You have access to 7 free modules. Upgrade to Premium for the complete journey.
                  </p>
                </div>
                <Button
                  onClick={() => navigate("/subscription")}
                  className="btn-primary whitespace-nowrap"
                  data-testid="upgrade-btn"
                >
                  Go Premium
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Modules List */}
        <div className="space-y-4">
          <h2 className="font-heading text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-primary" />
            All Modules
          </h2>
          
          {modules.map((module) => (
            <Card 
              key={module.id}
              className={`glass-card rounded-2xl border-0 transition-all ${
                module.is_locked 
                  ? 'opacity-60' 
                  : 'cursor-pointer hover:scale-[1.01] card-hover'
              } ${module.is_completed ? 'ring-2 ring-green-500/30' : ''}`}
              onClick={() => !module.is_locked && navigate(`/emotional-intelligence/${module.id}`)}
              data-testid={`module-card-${module.id}`}
            >
              <CardContent className="p-5">
                <div className="flex items-center gap-4">
                  {/* Status Icon */}
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    module.is_completed 
                      ? 'bg-green-500/10' 
                      : module.is_locked 
                        ? 'bg-muted' 
                        : 'bg-primary/10'
                  }`}>
                    {module.is_completed ? (
                      <CheckCircle2 className="w-6 h-6 text-green-500" />
                    ) : module.is_locked ? (
                      <Lock className="w-6 h-6 text-muted-foreground" />
                    ) : (
                      <Circle className="w-6 h-6 text-primary" />
                    )}
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs text-muted-foreground">Module {module.order}</span>
                      {module.is_free && (
                        <span className="text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 px-2 py-0.5 rounded-full">
                          Free
                        </span>
                      )}
                      {!module.is_free && !module.is_locked && (
                        <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                          Premium
                        </span>
                      )}
                    </div>
                    <h3 className="font-heading font-semibold text-foreground truncate">
                      {module.title}
                    </h3>
                    <p className="text-sm text-muted-foreground truncate">
                      {module.subtitle}
                    </p>
                    <div className="flex items-center gap-3 mt-2">
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {module.duration}
                      </span>
                    </div>
                  </div>
                  
                  {/* Arrow */}
                  {!module.is_locked && (
                    <ChevronRight className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Badge Preview */}
        {!hasBadge && (
          <Card className="glass-card rounded-3xl border-0 mt-10">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-amber-100 to-amber-200 dark:from-amber-900/30 dark:to-amber-800/30 flex items-center justify-center mx-auto mb-4 opacity-50">
                <Award className="w-10 h-10 text-amber-600" />
              </div>
              <h3 className="font-heading text-lg font-semibold text-foreground mb-2">
                Emotional Intelligence Mastery Badge
              </h3>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">
                Complete all 22 modules to earn this badge and demonstrate your commitment to emotional growth.
              </p>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default EmotionalIntelligencePage;
